// AI Drill Service for DeepSeek API integration
// Handles drill example generation with cost tracking and error handling

const DEEPSEEK_API_KEY = 'sk-7549b29d010a424a836d081a478552f4';
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';

// Calculate cost based on DeepSeek API tariff
export const calculateCost = (inputTokens, outputTokens, isCacheHit = false) => {
  const now = new Date();
  const beijingTime = new Date(now.getTime() + (8 * 60 * 60 * 1000));
  const hour = beijingTime.getUTCHours();
  const isDiscountTime = hour >= 0 && hour < 8;
  
  let inputCost, outputCost;
  
  if (isDiscountTime) {
    inputCost = isCacheHit ? 0.25 : 1.0;
    outputCost = 4.0;
  } else {
    inputCost = isCacheHit ? 0.5 : 2.0;
    outputCost = 8.0;
  }
  
  const totalInputCost = (inputTokens / 1000000) * inputCost;
  const totalOutputCost = (outputTokens / 1000000) * outputCost;
  
  return {
    inputCost: totalInputCost,
    outputCost: totalOutputCost,
    totalCost: totalInputCost + totalOutputCost,
    isDiscountTime
  };
};

// Generate drill examples for grammar units
export const generateGrammarUnitDrill = async (example, unitData, onProgress) => {
  const startTime = Date.now();
  
  try {
    // Extract grammar pattern from the example
    const grammarPattern = example.analysis?.match(/\(([SVOCM][^)]*)\)/g)?.map(tag => tag.slice(1, -1)) || [];
    const patternString = grammarPattern.join('+');
    
    const prompt = `Generate 3 similar English grammar examples for practice. 

Original example:
Sentence: "${example.sentence || example.en || example.example}"
Grammar analysis: "${example.analysis}"
Grammar pattern: ${patternString}
Unit topic: ${unitData.title?.english || unitData.title?.traditional || 'Grammar Unit'}

Requirements:
1. Create 3 new sentences with the SAME grammar pattern: ${patternString}
2. Use similar difficulty level and word tone
3. Maintain the same grammar category and structure
4. Each sentence should be natural and educational
5. Provide grammar analysis for each sentence in the format: "word (S) word (V) word (O)" etc.

CRITICAL: Return ONLY valid JSON array. Do not include any explanations, comments, or additional text before or after the JSON.

Return the response as a JSON array with this exact format:
[
  {
    "sentence": "example sentence 1",
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation", 
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 2", 
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 3",
    "analysis": "word (S) word (V) word (O)", 
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  }
]`;

    // Update progress
    onProgress?.({
      status: 'Generating examples...',
      timeElapsed: (Date.now() - startTime) / 1000,
      tokensUsed: 0,
      costIncurred: 0
    });

    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(`API Error: ${data.error?.message || 'Unknown error'}`);
    }

    const content = data.choices[0].message.content;
    
    // Clean and parse the JSON response
    let drillData;
    try {
      if (!content || content.trim() === '') {
        throw new Error('Empty response from API');
      }
      
      const jsonMatch = content.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        drillData = JSON.parse(jsonMatch[0]);
      } else {
        drillData = JSON.parse(content);
      }
    } catch (parseError) {
      console.error('JSON parsing failed:', parseError);
      console.log('Raw content:', content);
      
      // Fallback: Create mock drill data
      drillData = [
        {
          sentence: `${example.sentence || example.en || example.example} (Drill 1)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || example.zh || '練習1'}`,
            sc: `${example.translation?.sc || example.zh || '练习1'}`,
            en: `${example.sentence || example.en || example.example} (Drill 1)`
          }
        },
        {
          sentence: `${example.sentence || example.en || example.example} (Drill 2)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || example.zh || '練習2'}`,
            sc: `${example.translation?.sc || example.zh || '练习2'}`,
            en: `${example.sentence || example.en || example.example} (Drill 2)`
          }
        },
        {
          sentence: `${example.sentence || example.en || example.example} (Drill 3)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || example.zh || '練習3'}`,
            sc: `${example.translation?.sc || example.zh || '练习3'}`,
            en: `${example.sentence || example.en || example.example} (Drill 3)`
          }
        }
      ];
    }
    
    // Validate and clean drill data
    if (!Array.isArray(drillData) || drillData.length === 0) {
      throw new Error('Invalid drill data structure');
    }
    
    drillData = drillData.map((item, index) => {
      if (!item.sentence || !item.analysis || !item.translation) {
        return {
          sentence: `${example.sentence || example.en || example.example} (Drill ${index + 1})`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || example.zh || `練習${index + 1}`}`,
            sc: `${example.translation?.sc || example.zh || `练习${index + 1}`}`,
            en: `${example.sentence || example.en || example.example} (Drill ${index + 1})`
          }
        };
      }
      return item;
    });
    
    const timeElapsed = (Date.now() - startTime) / 1000;
    const inputTokens = data.usage.prompt_tokens;
    const outputTokens = data.usage.completion_tokens;
    const costInfo = calculateCost(inputTokens, outputTokens);
    
    // Final progress update
    onProgress?.({
      status: 'Completed',
      timeElapsed,
      tokensUsed: inputTokens + outputTokens,
      costIncurred: costInfo.totalCost,
      isDiscountTime: costInfo.isDiscountTime
    });
    
    return {
      drillExamples: drillData,
      stats: {
        timeElapsed,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: costInfo.totalCost,
        isDiscountTime: costInfo.isDiscountTime
      }
    };
    
  } catch (error) {
    console.error('AI Drill generation failed:', error);
    
    // Return fallback data
    const fallbackData = [
      {
        sentence: `${example.sentence || example.en || example.example} (Drill 1)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || example.zh || '練習1'}`,
          sc: `${example.translation?.sc || example.zh || '练习1'}`,
          en: `${example.sentence || example.en || example.example} (Drill 1)`
        }
      },
      {
        sentence: `${example.sentence || example.en || example.example} (Drill 2)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || example.zh || '練習2'}`,
          sc: `${example.translation?.sc || example.zh || '练习2'}`,
          en: `${example.sentence || example.en || example.example} (Drill 2)`
        }
      },
      {
        sentence: `${example.sentence || example.en || example.example} (Drill 3)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || example.zh || '練習3'}`,
          sc: `${example.translation?.sc || example.zh || '练习3'}`,
          en: `${example.sentence || example.en || example.example} (Drill 3)`
        }
      }
    ];
    
    const timeElapsed = (Date.now() - startTime) / 1000;
    
    onProgress?.({
      status: 'Error - Using fallback examples',
      timeElapsed,
      tokensUsed: 0,
      costIncurred: 0
    });
    
    return {
      drillExamples: fallbackData,
      stats: {
        timeElapsed,
        tokensUsed: 0,
        costIncurred: 0,
        isDiscountTime: false
      },
      error: error.message
    };
  }
};

// Generate drill examples for grammar topics
export const generateGrammarTopicDrill = async (example, topicData, onProgress) => {
  const startTime = Date.now();
  
  try {
    const grammarPattern = example.analysis?.match(/\(([SVOCM][^)]*)\)/g)?.map(tag => tag.slice(1, -1)) || [];
    const patternString = grammarPattern.join('+');
    
    const prompt = `Generate 3 similar English grammar examples for practice. 

Original example:
Sentence: "${example.sentence}"
Grammar analysis: "${example.analysis}"
Grammar pattern: ${patternString}
Topic: ${topicData.title?.english || topicData.title?.traditional || 'Grammar Topic'}

Requirements:
1. Create 3 new sentences with the SAME grammar pattern: ${patternString}
2. Use similar difficulty level and word tone
3. Maintain the same grammar category and structure
4. Each sentence should be natural and educational
5. Provide grammar analysis for each sentence in the format: "word (S) word (V) word (O)" etc.

CRITICAL: Return ONLY valid JSON array. Do not include any explanations, comments, or additional text before or after the JSON.

Return the response as a JSON array with this exact format:
[
  {
    "sentence": "example sentence 1",
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation", 
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 2", 
    "analysis": "word (S) word (V) word (O)",
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  },
  {
    "sentence": "example sentence 3",
    "analysis": "word (S) word (V) word (O)", 
    "translation": {
      "tc": "Traditional Chinese translation",
      "sc": "Simplified Chinese translation",
      "en": "English original"
    }
  }
]`;

    onProgress?.({
      status: 'Generating examples...',
      timeElapsed: (Date.now() - startTime) / 1000,
      tokensUsed: 0,
      costIncurred: 0
    });

    const response = await fetch(DEEPSEEK_API_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${DEEPSEEK_API_KEY}`
      },
      body: JSON.stringify({
        model: 'deepseek-chat',
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.7,
        max_tokens: 1000
      })
    });

    const data = await response.json();
    
    if (!response.ok) {
      throw new Error(`API Error: ${data.error?.message || 'Unknown error'}`);
    }

    const content = data.choices[0].message.content;
    
    let drillData;
    try {
      if (!content || content.trim() === '') {
        throw new Error('Empty response from API');
      }
      
      const jsonMatch = content.match(/\[[\s\S]*\]/);
      if (jsonMatch) {
        drillData = JSON.parse(jsonMatch[0]);
      } else {
        drillData = JSON.parse(content);
      }
    } catch (parseError) {
      console.error('JSON parsing failed:', parseError);
      
      drillData = [
        {
          sentence: `${example.sentence} (Drill 1)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || '練習1'}`,
            sc: `${example.translation?.sc || '练习1'}`,
            en: `${example.sentence} (Drill 1)`
          }
        },
        {
          sentence: `${example.sentence} (Drill 2)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || '練習2'}`,
            sc: `${example.translation?.sc || '练习2'}`,
            en: `${example.sentence} (Drill 2)`
          }
        },
        {
          sentence: `${example.sentence} (Drill 3)`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || '練習3'}`,
            sc: `${example.translation?.sc || '练习3'}`,
            en: `${example.sentence} (Drill 3)`
          }
        }
      ];
    }
    
    if (!Array.isArray(drillData) || drillData.length === 0) {
      throw new Error('Invalid drill data structure');
    }
    
    drillData = drillData.map((item, index) => {
      if (!item.sentence || !item.analysis || !item.translation) {
        return {
          sentence: `${example.sentence} (Drill ${index + 1})`,
          analysis: example.analysis,
          translation: {
            tc: `${example.translation?.tc || `練習${index + 1}`}`,
            sc: `${example.translation?.sc || `练习${index + 1}`}`,
            en: `${example.sentence} (Drill ${index + 1})`
          }
        };
      }
      return item;
    });
    
    const timeElapsed = (Date.now() - startTime) / 1000;
    const inputTokens = data.usage.prompt_tokens;
    const outputTokens = data.usage.completion_tokens;
    const costInfo = calculateCost(inputTokens, outputTokens);
    
    onProgress?.({
      status: 'Completed',
      timeElapsed,
      tokensUsed: inputTokens + outputTokens,
      costIncurred: costInfo.totalCost,
      isDiscountTime: costInfo.isDiscountTime
    });
    
    return {
      drillExamples: drillData,
      stats: {
        timeElapsed,
        tokensUsed: inputTokens + outputTokens,
        costIncurred: costInfo.totalCost,
        isDiscountTime: costInfo.isDiscountTime
      }
    };
    
  } catch (error) {
    console.error('AI Drill generation failed:', error);
    
    const fallbackData = [
      {
        sentence: `${example.sentence} (Drill 1)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || '練習1'}`,
          sc: `${example.translation?.sc || '练习1'}`,
          en: `${example.sentence} (Drill 1)`
        }
      },
      {
        sentence: `${example.sentence} (Drill 2)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || '練習2'}`,
          sc: `${example.translation?.sc || '练习2'}`,
          en: `${example.sentence} (Drill 2)`
        }
      },
      {
        sentence: `${example.sentence} (Drill 3)`,
        analysis: example.analysis,
        translation: {
          tc: `${example.translation?.tc || '練習3'}`,
          sc: `${example.translation?.sc || '练习3'}`,
          en: `${example.sentence} (Drill 3)`
        }
      }
    ];
    
    const timeElapsed = (Date.now() - startTime) / 1000;
    
    onProgress?.({
      status: 'Error - Using fallback examples',
      timeElapsed,
      tokensUsed: 0,
      costIncurred: 0
    });
    
    return {
      drillExamples: fallbackData,
      stats: {
        timeElapsed,
        tokensUsed: 0,
        costIncurred: 0,
        isDiscountTime: false
      },
      error: error.message
    };
  }
};
